import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../../auth/authContext";
import { ApplicationPage, ApplicationSectionHeader, ApplicationStateRegion, ApplicationViewHeader } from "../../../components/application";
import { DataTable } from "../../../components/data-display";
import ProjectFacilitatorNavigation from "../components/ProjectFacilitatorNavigation";
import { useProjectFacilitatorMembers } from "../hooks/useProjectFacilitatorMembers";
import { projectMembershipRoleLabel } from "../utils/projectMembershipPresentation";
import { resolveProgramFacilitatePresentationAdapter } from "../evidence/programFacilitatePresentationAdapterRegistry";
import { projectInstitutionalContextLabel } from "../utils/projectPresentation";

const stateLabel = state => ({
  applicability_unknown: "Participation not established",
  in_progress: "In progress",
  completed: "Completed",
}[state] || "Participation not established");
const contextKey = value => value?.id || value?.name || "";

export default function ProjectFacilitatorMembers() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5001/api";
  const { project, members, programs, loading, error } = useProjectFacilitatorMembers({ apiUrl, slug, token: user?.token });
  const [programId, setProgramId] = useState("");
  const [search, setSearch] = useState("");
  const [role, setRole] = useState("");
  const [progressState, setProgressState] = useState("");
  const [cohortId, setCohortId] = useState("");
  const [groupId, setGroupId] = useState("");
  const selectedProgram = programs.find((item) => item.id === programId) || programs[0];
  const identifierDisclosed = members.some((member) =>
    Object.prototype.hasOwnProperty.call(member, "participantIdentifier"));
  const cohorts = [...new Map(members.filter(item => item.cohort?.name).map(item => [contextKey(item.cohort), item.cohort])).values()];
  const groups = [...new Map(members.filter(item => item.group?.name).map(item => [contextKey(item.group), item.group])).values()];
  const hasCohorts = cohorts.length > 0;
  const hasGroups = groups.length > 0;

  const rows = useMemo(() => members.map((member) => {
    const progress = member.programs.find((item) => item.programId === selectedProgram?.id);
    return {
      ...member,
      progress,
      progressLabel: stateLabel(progress?.applicabilityState),
      completion: progress?.currentAttempt?.completionPercentage ?? null,
      lastActivityAt: progress?.currentAttempt?.lastActivityAt || null,
      evidence: member.evidenceByProgram?.[selectedProgram?.id] || null,
    };
  }).filter((member) => {
    const query = search.trim().toLocaleLowerCase();
    const searchable = [member.displayIdentity, member.email, member.participantIdentifier].filter(Boolean).join(" ").toLocaleLowerCase();
    return (!query || searchable.includes(query))
      && (!role || member.role === role)
      && (!progressState || member.progress?.applicabilityState === progressState)
      && (!cohortId || contextKey(member.cohort) === cohortId)
      && (!groupId || contextKey(member.group) === groupId);
  }), [cohortId, groupId, members, progressState, role, search, selectedProgram]);

  if (loading) return <ApplicationPage><ApplicationStateRegion message="Loading Project members..." status="loading" /></ApplicationPage>;
  if (error || !project) return <ApplicationPage><ApplicationStateRegion message={error || "Project members are unavailable."} status="error" /></ApplicationPage>;

  const genericColumns = [
    { key: "displayIdentity", label: "Member" },
    ...(identifierDisclosed ? [{ key: "participantIdentifier", label: "Participant ID" }] : []),
    { key: "role", label: "Project role", getValue: row => projectMembershipRoleLabel(row.role) },
    ...(hasCohorts ? [{ key: "cohort", label: "Cohort", getValue: row => row.cohort?.name || "—", sortValue: row => row.cohort?.name || "" }] : []),
    ...(hasGroups ? [{ key: "group", label: "Group", getValue: row => row.group?.name || "—", sortValue: row => row.group?.name || "" }] : []),
    { key: "progressLabel", label: "Program progress" },
    { key: "completion", label: "Completion", getValue: row => row.completion === null ? "—" : `${row.completion}%`, sortValue: row => row.completion },
    { key: "lastActivityAt", label: "Recent activity", getValue: row => row.lastActivityAt ? new Date(row.lastActivityAt).toLocaleDateString() : "—" },
    { key: "action", label: "", sortable: false, getValue: row => <Link className="av-project-facilitator-members__action" to={`/app/facilitate/project/${slug}/member/${row.membershipId}`}>View member</Link> },
  ];
  const presentationAdapter = resolveProgramFacilitatePresentationAdapter(selectedProgram);
  const columns = presentationAdapter?.createMemberColumns
    ? presentationAdapter.createMemberColumns({ projectSlug: slug })
    : genericColumns;
  return (
    <ApplicationPage className="av-project-facilitator-members" variant="workspace">
      <ApplicationViewHeader contextLabel="Project facilitation" title={project.title} description={projectInstitutionalContextLabel(project) || undefined} />
      <Link className="av-project-facilitator-overview__back" to="/app/facilitate">Back to Facilitate</Link>
      <ProjectFacilitatorNavigation slug={slug} showCohortResults={Boolean(presentationAdapter?.key === "ksu-building-your-clinical-ear.facilitate-presentation@1")} />
      <section aria-labelledby="project-members-title">
        <ApplicationSectionHeader title="Members" titleId="project-members-title" description={presentationAdapter?.membersDescription || "Review Project membership and generic Program progress."} />
        <div className="av-project-facilitator-members__filters">
          <label><span>Search members</span><input value={search} onChange={event => setSearch(event.target.value)} type="search" /></label>
          {programs.length > 1 ? <label><span>Program</span><select value={selectedProgram?.id || ""} onChange={event => setProgramId(event.target.value)}>{programs.map(item => <option key={item.id} value={item.id}>{item.title}</option>)}</select></label> : null}
          <label><span>Project role</span><select value={role} onChange={event => setRole(event.target.value)}><option value="">All roles</option>{[...new Set(members.map(item => item.role).filter(Boolean))].map(item => <option key={item} value={item}>{projectMembershipRoleLabel(item)}</option>)}</select></label>
          {hasCohorts ? <label><span>Cohort</span><select value={cohortId} onChange={event => setCohortId(event.target.value)}><option value="">All cohorts</option>{cohorts.map(item => <option key={contextKey(item)} value={contextKey(item)}>{item.name}</option>)}</select></label> : null}
          {hasGroups ? <label><span>Group</span><select value={groupId} onChange={event => setGroupId(event.target.value)}><option value="">All groups</option>{groups.map(item => <option key={contextKey(item)} value={contextKey(item)}>{item.name}</option>)}</select></label> : null}
          <label><span>Progress</span><select value={progressState} onChange={event => setProgressState(event.target.value)}><option value="">All states</option><option value="applicability_unknown">Participation not established</option><option value="in_progress">In progress</option><option value="completed">Completed</option></select></label>
        </div>
        <DataTable
          caption="Project members"
          columns={columns}
          emptyMessage="No Project members match the current view."
          minWidth="58rem"
          onRowActivate={row => navigate(`/app/facilitate/project/${slug}/member/${row.membershipId}`)}
          rowKey="membershipId"
          rows={rows}
          sortable
          stickyHeader
        />
      </section>
    </ApplicationPage>
  );
}
